<template>
  <ErrorBoundary
    :component="() => import('pages/NotFoundPage.vue')"
    :is-error="hasError"
  >
    <div
      v-if="!hasError"
      class="preview-page"
    >
      <div
        class="preview-page__blur"
        :style="{ backgroundImage: backImage }"
      />
      <div
        v-if="bookPreview.trackingCodes && bookPreview.trackingCodes.headerCode"
        v-html="bookPreview.trackingCodes.headerCode"
      />
      <PreviewPageHeader v-if="$q.screen.gt.sm" />
      <div class="preview-page__container row full-height">
        <div
          v-if="$q.screen.gt.sm"
          class="col-2"
        >
          <div class="full-height purchase-buttons column justify-end">
            <div>
              <AppBtn
                icon-right="fa-solid fa-download"
                icon-right-size="18px"
                class="no-hover text-16-700 q-mb-md border-radius-20"
                color="white"
                text-color="grey"
                padding="19px 25px"
                :label="$t('button.downloadBook')"
                @click="onClickDownload"
              />
            </div>
            <div>
              <AppBtn
                v-if="isShare"
                icon-right="fl:share"
                icon-right-size="18px"
                class="no-hover text-16-700 border-radius-20"
                color="white"
                text-color="grey"
                padding="19px 25px"
                :label="$t('label.share')"
                @click="showDownloadPopup = true"
              />
            </div>
          </div>
        </div>
        <div class="col-md-8 col-12">
          <div class="preview-page__content">
            <div
              v-if="$q.platform.is.mobile"
              class="q-pa-lg flex items-center justify-between"
            >
              <q-img
                class="preview-page-header__logo"
                src="/public/ki-logo.svg"
                height="30px"
                width="44px"
              />
              <AppBtn
                icon-right="fa-solid fa-download"
                icon-right-size="18px"
                class="no-hover text-13-700 border-radius-20"
                color="white"
                text-color="grey"
                padding="14px 25px"
                :label="$t('button.downloadBook')"
                @click="onClickDownload"
              />
            </div>
            <q-img
              width="100%"
              :ratio="530 / 750"
              :src="coverUrl"
            />

            <div :class="['text-container bg-white', { 'q-mb-xl': $q.platform.is.desktop }]">
              <FormattedTextStyles
                :content="bookPreview.content"
                :meta-fields="bookPreview.metaFields"
                class="formatted-text"
              />

              <div v-if="limitedAccess">
                <PayBlock
                  :access-type="accessType"
                  @refresh-book="fetchBook"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="bookPreview.trackingCodes && bookPreview.trackingCodes.footerCode"
        v-html="bookPreview.trackingCodes.footerCode"
      />
      <AppModal
        :model-value="showDownloadPopup"
        show-close-btn
        :title="$t('title.openBook')"
        :content-style="{ 'max-width': '600px', width: '100%', position: 'relative' }"
        parent-class="q-pa-lg"
        header-class="bg-white"
        body-class="flex-1"
        @hide="showDownloadPopup = false"
      >
        <div class="q-pa-sm q-pa-sm-lg">
          <PayBlock
            :access-type="accessType"
            no-shadow
            @success="showDownloadPopup = false"
            @refresh-book="fetchBook"
          />
        </div>
      </AppModal>
    </div>
  </ErrorBoundary>
</template>

<script setup>
import { useAccountStore } from 'stores/account';
// http://localhost:8080/book/rsl-moto-ds24-book?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2FwaS5mdW5raXRvb2xzLmRldiIsImlhdCI6MTc0MDA0Njc5NywibmJmIjoxNzQwMDQ2Nzk3LCJleHAiOjE3NDA2NTE1OTcsImRhdGEiOnsidXNlcklkIjo4N319.aO1djIbJdSJkmVJoX7gmkjK3HmOLoTmy_gRwKrWNb4s
import { useDocumentsStore } from 'stores/documents';
import {
  getReaderAccessToken,
  setCookie,
  setReaderAccessToken,
  unsetTokens,
} from 'src/helpers/cookies';
import { storeToRefs } from 'pinia';
import ErrorBoundary from 'components/ErrorBoundary.vue';
import {
  computed, onMounted, ref, inject,
} from 'vue';
import PreviewPageHeader from 'pages/preview/PreviewPageHeader.vue';
import FormattedTextStyles from 'components/FormattedTextStyles.vue';
import PayBlock from 'components/PayBlock.vue';
import AppBtn from 'components/AppBtn.vue';
import BOOK_ACCESS_TYPES from 'src/enums/bookAccessTypes';
import AppModal from 'components/AppModal.vue';
import { useRoute, useRouter } from 'vue-router';
import handleError from 'src/helpers/handleError';
import { useMeta } from 'quasar';
import { ROUTES } from 'src/enums';

defineOptions({
  async preFetch({
    store,
    ssrContext,
    currentRoute,
    redirect,
  }) {
    try {
      const host = ssrContext
        ? ssrContext.req.get('host')
          .split(':')[0]
        : document.location.host;

      const accountStore = useAccountStore(store);
      const documentsStore = useDocumentsStore(store);

      const isServiceHost = ssrContext
        ? ssrContext.req.headers.host.includes(process.env.SERVICE_DOMAIN)
        : window.location.href.includes(process.env.SERVICE_DOMAIN);

      const domain = !isServiceHost
        ? host
        : accountStore.account?.domain;

      const accessToken = currentRoute.query.token;

      if (accessToken) {
        setCookie('accessToken', accessToken, {
          domain: host,
        }, ssrContext);
      }

      await documentsStore.getBookPreviewBySlug({
        domain,
        bookSlug: currentRoute.params.bookSlug,
        readerAccessToken: getReaderAccessToken(currentRoute.params.bookSlug, ssrContext),
        preview: ssrContext.req.query?.preview,
      });
    } catch (error) {
      console.log(error);
      const { status } = error.response;
      if (status === 400) {
        redirect({ name: ROUTES.BOOKS.NOT_FOUND });
      }
    }
  },
});

const route = useRoute();
const router = useRouter();

const axios = inject('axios');
const isServiceHost = inject('isServiceHost');

const documentsStore = useDocumentsStore();
const accountStore = useAccountStore();
const { bookPreview } = storeToRefs(documentsStore);
const { account } = storeToRefs(accountStore);

const showDownloadPopup = ref(false);

const hasError = computed(() => bookPreview.value === null);

const coverUrl = computed(() => bookPreview.value.metaFields?.coverImageFileGuid?.url || bookPreview.value?.coverImageUrl);

const accessType = computed(() => bookPreview.value.access.type);

const isShare = computed(() => accessType.value === BOOK_ACCESS_TYPES.SHARE);

const backImage = computed(() => (coverUrl.value
  ? `url(${coverUrl.value})`
  : 'none'));

const limitedAccess = computed(() => bookPreview.value.access.limitedAccess);

const readerAccessToken = computed(() => getReaderAccessToken(bookPreview.value.slug));

function onClickDownload() {
  if (limitedAccess.value) {
    showDownloadPopup.value = true;
  } else {
    const url = `${process.env.API_HOST}/api/public/route/download?fileType=PDF&url=${bookPreview.value.publicUrl}`;

    const name = bookPreview.value.metaFields.bookHeading || bookPreview.value.title;
    downloadFile(url, name, readerAccessToken.value);
  }
}

async function downloadFile(url, fileName, readerAccessToken) {
  try {
    const response = await axios({
      url,
      method: 'GET',
      responseType: 'blob',
      headers: {
        ReaderAccessToken: readerAccessToken || '',
      },
    });

    const href = window.URL.createObjectURL(response.data);
    const anchorElement = document.createElement('a');

    anchorElement.href = href;
    anchorElement.download = fileName;
    document.body.append(anchorElement);
    anchorElement.click();
    anchorElement.remove();
    window.URL.revokeObjectURL(href);
  } catch (error) {
    handleError(error);
  }
}

async function updateStatistic() {
  try {
    await axios.post(`public/books/${bookPreview.value.guid}/statistic`);
  } catch {
  }
}

async function getTokenFromAccessHash(accessHash) {
  try {
    const { data } = await axios.post(`public/books/${bookPreview.value.guid}/temporary-token`, {
      accessData: {
        type: BOOK_ACCESS_TYPES.LINK,
        data: {
          hash: accessHash,
        },
      },
    });
    setReaderAccessToken(bookPreview.value.slug, data.data.token);
    await fetchBook();
  } catch (error) {
    handleError(error);
  }
}

async function fetchBook() {
  try {
    const publicHost = bookPreview.value
      ? bookPreview.value.publicUrl.split('/')[2]
      : account.value?.domain;

    const domain = isServiceHost
      ? publicHost
      : document.location.host;

    const query = { ...route.query };
    await documentsStore.getBookPreviewBySlug({
      domain,
      bookSlug: bookPreview.value.slug,
      readerAccessToken: readerAccessToken.value,
      preview: query?.preview,
    });
  } catch (error) {
    handleError(error);
  }
}

useMeta(() => ({
  title: bookPreview.value?.title,
  meta: {
    // description: {
    //   name: 'description',
    //   content: bookPreview.value?.metaFields.metaDescription || '',
    // },
    author: {
      name: 'author',
      content: bookPreview.value?.author.displayName || '',
    },
    keywords: {
      name: 'keywords',
      content: bookPreview.value?.metaFields.metaKeywords || '',
    },
  },
}));

onMounted(async () => {
  if (bookPreview.value?.faviconUrl) {
    document.querySelectorAll('link[rel="icon"]').forEach((link) => {
      link.href = bookPreview.value.faviconUrl;
    });
  }

  const query = { ...route.query };
  if (query.token !== undefined) {
    delete query.token;
    await router.replace({ query });

    if (limitedAccess.value) {
      unsetTokens();
    }
  }

  if (!bookPreview.value) {
    return;
  }

  updateStatistic();

  const { accessHash } = route.params;

  if (accessHash) {
    getTokenFromAccessHash(accessHash);
  }
});

</script>

<style scoped lang="scss">
.formatted-text {
  ::v-deep .typogarphy-generated-text {
    padding: 45px 60px;
  }
}

::v-deep.render {
  padding-bottom: 65px;

  .inner {
    overflow: hidden;

    .img-container {
      display: contents !important;
      height: auto;
    }

    #intro {
      height: 640px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
  }
}

.preview-page {
  font-family: 'Mitr', sans-serif;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  &__blur {
    height: 100vh;
    width: 100vw;
    position: absolute;
    -webkit-mask: radial-gradient(
        circle,
        #0000 150px,
        rgba(0, 0, 0, 0.9) 160px
    );
    filter: blur(70px);
    opacity: 0.4;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-backface-visibility: hidden;
    -moz-backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    -moz-transform: translate3d(0, 0, 0);
  }

  ::v-deep .body-heading,
  ::v-deep .cover-img {
    border-radius: 20px 20px 0 0 !important;

    @media (max-width: $breakpoint-sm) {
      border-radius: 0 !important;
    }
  }

  .text-container {
    border-radius: 0 0 20px 20px;
    overflow: hidden;

    @media (max-width: $breakpoint-sm) {
      border-radius: 0;
    }
  }

  &__container {
    padding-top: 60px;
    overflow: scroll;

    @media (max-width: $breakpoint-sm) {
      padding-top: 0;
    }
  }

  &__content {
    position: relative;
    height: 100%;
    max-width: 860px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    border-radius: 20px 20px 0 0;
    overflow: hidden;

    @media (max-width: $breakpoint-sm) {
      border-radius: 0;
    }
  }
}

.purchase-buttons {
  padding: 0 0 30px 30px;
  position: absolute;
  bottom: 0;
}

::v-deep {
  .purchase-buttons {
    button {
      .q-btn__wrapper {
        &::before {
          display: none;
        }

        .q-btn__content {
          i {
            margin-left: 10px;
          }
        }
      }
    }
  }
}
</style>
